/* eslint no-unused-expressions: 0 */
import React, { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Footer from '../../components/Footer/Footer';
import TopBar from '../../components/TopBar/TopBar';
import ProductMenu from '../../components/ProductMenu';
import ScenePopupManager from '../../components/ScenePopupManager';
import Scene from '../../scene';
import ProductMenuCover from '../../components/ProductMenuCover';
import tacHelpers from '../../state/tac/tacHelpers';
import WallSizeInput from '../../components/WallSizeInput/WallSizeInput';
import platform from '../../util/platform';
import {
  DROP_ITEM,
  IFRAME_RESIZED,
  TRASH_CAN_HOVER,
  WALL_RESIZED,
} from '../../settings/events';
import draggingUtil from '../../util/dragging';
import * as supportedEvents from '../../util/supportedEvents';
import emitter from '../../emitter';
import constants from '../../settings/constants';
import { SCENE_VIEW_ID } from '../../constants';
import {
  selectIsWallResizerActive,
  selectSceneSheetVariant,
  selectShowSceneSheet,
} from '../../state/scene/sceneSelectors';
import { selectTac, selectWallSize } from '../../state/tac/tacSelectors';
import styles from './SceneView.module.less';
import SupplyBanner from '../../components/SupplyBanner/SupplyBanner';
import {
  selectIsKiosk,
  selectIsMobile,
  selectIsMobilePortrait,
  selectIsTabletPortrait,
} from '../../state/userAgent/userAgentSelectors';
import { findDOMNode } from 'react-dom';
import { actionHideSceneSheet } from '../../state/scene';
import { Point } from '../../generalTypes';
import JoyRide from '../../components/JoyRide/JoyRide';
import { selectFilterIntroPopupVisible } from '../../state/popups/popupsSelectors';
import { selectInitSuccessful } from '../../state/init/initSelectors';
import {
  actionHideIntroPopups,
  actionSetFilterIntroPopupBeVisible,
} from '../../state/popups/popupsActions';
import { thunkSetSceneRect } from '../../state/scene/sceneThunks';
import { thunkSetWall } from '../../state/tac/tacThunks';
import store from '../../state';
import { SheetObject } from '../../state/sheets/sheetTypes';
import { actionEnqueueSheet } from '../../state/sheets/sheetActions';

const { MOUSE_SUPPORT, POINTER_SUPPORT, TOUCH_SUPPORT } = supportedEvents;

export type Props = {
  buildScene: any;
  captureScene: any;
};

const SceneView: FunctionComponent<Props> = ({ buildScene, captureScene }) => {
  type Meta = { origin: string; isPersistent: true };

  const root = React.useRef<HTMLDivElement>(null);
  const canvasContainer = React.useRef<HTMLDivElement>(null);
  const productMenuCover = React.useRef<HTMLDivElement>(null);
  const leftContainer = React.useRef<HTMLDivElement>(null);
  const scene = React.useRef<any>(null);

  const [isDragging, setIsDragging] = React.useState(false);
  const [showTrashcan, setShowTrashcan] = React.useState(false);
  const [enlargeTrashcan, setEnlargeTrashcan] = React.useState(false);
  const [sceneAvailable, setSceneAvailable] = React.useState(false);
  const [topWallResizerInputPosition, setTopWallResizerInputPosition] =
    React.useState<{ x: number; y: number } | null>(null);
  const [rightWallResizerInputPosition, setRightWallResizerInputPosition] =
    React.useState<{ x: number; y: number } | null>(null);
  const wallSize = useSelector(selectWallSize);

  const isMobile = useSelector(selectIsMobile);
  const isMobilePortrait = useSelector(selectIsMobilePortrait);
  const isTabletPortrait = useSelector(selectIsTabletPortrait);
  const isKiosk = useSelector(selectIsKiosk);
  const tac = useSelector(selectTac);
  const wallResizerActive = useSelector(selectIsWallResizerActive);
  const filterIntroPopupsVisible = useSelector(selectFilterIntroPopupVisible);
  const initSuccessful = useSelector(selectInitSuccessful);
  const showingSceneSheet = useSelector(selectShowSceneSheet);
  const sceneSheetVariant = useSelector(selectSceneSheetVariant);

  const dispatch = useDispatch();
  const setWall = (
    { width, height }: { width?: number; height?: number },
    meta: Meta
  ) => dispatch(thunkSetWall({ width, height }, meta));

  const _getRects = () => {
    if (!root.current || !canvasContainer.current)
      throw new Error('Some refs are not defined.');
    const view = root.current.getBoundingClientRect();
    const sceneDimensions = canvasContainer.current.getBoundingClientRect();
    // eslint-disable-next-line react/no-find-dom-node
    const trashcan = findDOMNode(
      productMenuCover.current
      // @ts-ignore
    )?.getBoundingClientRect();

    const viewRect = {
      x: view.left,
      y: view.top,
      width: view.width,
      height: view.height,
    };

    const sceneRect = {
      // the -1 is to account for the border around the app
      x: sceneDimensions.left - viewRect.x,
      y: sceneDimensions.top - viewRect.y,
      width: sceneDimensions.width,
      height: sceneDimensions.height,
    };
    // @ts-ignore
    store.dispatch(thunkSetSceneRect(sceneRect));

    const trashcanRect = {
      x: trashcan.left - viewRect.x,
      y: trashcan.top - viewRect.y,
      width: trashcan.width,
      height: trashcan.height,
    };

    return {
      viewRect,
      trashcanRect,
    };
  };

  const onDragStart = () => {
    draggingUtil.addBodyClass();
    setIsDragging(true);
  };

  const onDragEnd = () => {
    draggingUtil.removeBodyClass();
    setIsDragging(false);
    setShowTrashcan(false);
    setEnlargeTrashcan(false);
  };

  const stopDrag = (e: any) => {
    e = e.nativeEvent || e;

    const pixiEvent = {
      data: {
        global: {
          x: e.pageX,
          y: e.pageY,
        },
      },
    };

    emitter.emit(DROP_ITEM, pixiEvent);
  };

  const abortDrag = () => emitter.emit(DROP_ITEM, { abort: true });

  const resize = () => scene.current?.resizeScene(_getRects());

  const onTrashcanHover = (hover: boolean, fromScene: boolean) => {
    const updatedEnlargeTrashcan = hover && fromScene;
    const updatedShowTrashcan = !hover || fromScene;

    if (
      updatedEnlargeTrashcan !== enlargeTrashcan ||
      updatedShowTrashcan !== updatedEnlargeTrashcan
    ) {
      setEnlargeTrashcan(updatedEnlargeTrashcan);
      setShowTrashcan(updatedShowTrashcan);
    }
  };

  const setScene = () => (buildScene ? activateBuildScene() : generateScene());

  const prepScene = () => {
    if (!scene.current) return;

    const sceneCanvas = scene.current.view;
    const measurementsCanvas = scene.current.measurementLayer.view;

    sceneCanvas.className = styles.sceneCanvas;
    measurementsCanvas.className = styles.measurementLayer;

    canvasContainer.current?.appendChild(sceneCanvas);
    root.current?.appendChild(measurementsCanvas);
  };

  const preventScrollOnTouchMove = () => {
    root.current?.addEventListener(
      'touchmove',
      event => {
        event.preventDefault();
      },
      { passive: false }
    );
  };

  const activateBuildScene = () => {
    if (isKiosk) {
      preventScrollOnTouchMove();
    }
    scene.current = buildScene;
    scene.current?.activate({ onDragStart, onDragEnd });
  };

  const generateScene = () => {
    if (isKiosk) {
      preventScrollOnTouchMove();
    }
    return (scene.current = new Scene({
      onDragStart,
      onDragEnd,
      ..._getRects(),
    }));
  };

  React.useEffect(() => {
    emitter.on(DROP_ITEM, onDragEnd);
    emitter.on(IFRAME_RESIZED, resize);
    emitter.on(TRASH_CAN_HOVER, onTrashcanHover);
    emitter.on(WALL_RESIZED, onWallResized);

    setScene();
    prepScene();
    setSceneAvailable(true);

    return () => {
      emitter.off(IFRAME_RESIZED, resize);
      emitter.off(TRASH_CAN_HOVER, onTrashcanHover);
      emitter.off(DROP_ITEM, onDragEnd);
      emitter.off(WALL_RESIZED, onWallResized);

      scene.current && scene.current.deactivate();
      dispatch(actionHideSceneSheet());
      onDragEnd();
    };
  }, []);

  const onNextViewClick = () => captureScene(scene.current);

  const forwardEvent = (e: any) => {
    const ev = e.nativeEvent || e;

    ev.stopPropagation();

    if (ev.cancelable) ev.preventDefault();

    const event = new ev.constructor(ev.type, ev);

    scene.current?.view.dispatchEvent(event);
  };

  const abortIfTouchExit = (event: any) => {
    const { pageX, pageY } = event.touches[0];
    const { width, height } = _getRects().viewRect;

    if (pageX < width && pageX > 0 && pageY < height && pageY > 0) {
      return;
    }
    abortDrag();
  };

  const shouldPopOut = (dimension: string, measurement: number) =>
    isMobile && measurement < constants.WALL[dimension].max * 0.5;

  const getTopWallResizerInputPosition = (
    wallSizeHeight: number,
    x1: number,
    x2: number,
    y1: number
  ) => ({
    x: isMobile ? x1 - 1 : x1 + (x2 - x1) / 2,
    y: y1 - +!shouldPopOut('height', wallSizeHeight),
  });

  const getRightWallResizerInputPosition = (
    wallSizeWidth: number,
    x2: number,
    y2: number,
    y3: number
  ) => ({
    x: x2 + +!shouldPopOut('width', wallSizeWidth),
    y: isMobile ? y3 + 1 : y2 + (y3 - y2) / 2,
  });

  /**
   * Returns que for joy ride
   * @returns EventObject[]
   */
  const getQueue = () => [
    {
      condition: () => initSuccessful && !showingSceneSheet,
      func: () => dispatch(actionHideIntroPopups()),
    },
    {
      condition: () => filterIntroPopupsVisible,
      func: () => dispatch(actionSetFilterIntroPopupBeVisible(false)),
    },
  ];

  const onWallResized = ({ points }: { points: Point[] }) => {
    const [{ x: x1, y: y1 }, { x: x2, y: y2 }, { y: y3 }] = points;

    setTopWallResizerInputPosition(
      getTopWallResizerInputPosition(wallSize.height, x1, x2, y1)
    );
    setRightWallResizerInputPosition(
      getRightWallResizerInputPosition(wallSize.width, x2, y2, y3)
    );
  };

  const onComplete = (type: string) => (value: number) =>
    setWall({ [type]: value }, { origin: 'type', isPersistent: true });

  const getWallResizerInput = () => {
    const limits = tacHelpers.getWallResizingLimits(tac);

    if (!topWallResizerInputPosition || !rightWallResizerInputPosition) return;

    const { y: tY, x: tX } = topWallResizerInputPosition;
    const { y: rY, x: rX } = rightWallResizerInputPosition;

    return (
      <>
        <WallSizeInput
          val={wallSize.width}
          onComplete={onComplete('width')}
          top={tY}
          left={tX}
          isHorizontal={true}
          max={limits?.blockedX ? limits.max.x : constants.WALL.width.max}
          min={
            Math.max(limits.max.x, constants.WALL.width.min) ||
            constants.WALL.width.min
          }
          kiosk={platform.isKiosk}
          poppedOut={shouldPopOut('height', wallSize.height)}
        />
        <WallSizeInput
          onComplete={onComplete('height')}
          val={wallSize.height}
          top={rY}
          left={rX}
          max={constants.WALL.height.max}
          min={
            Math.max(limits.max.y, constants.WALL.height.min) ||
            constants.WALL.height.min
          }
          kiosk={platform.isKiosk}
          poppedOut={shouldPopOut('width', wallSize.width)}
        />
      </>
    );
  };

  const renderWallResizerInput = () =>
    rightWallResizerInputPosition &&
    topWallResizerInputPosition &&
    wallResizerActive &&
    getWallResizerInput();

  const renderScenePopupManager = () =>
    sceneAvailable && (
      <ScenePopupManager
        getItemSceneBounds={scene.current?.getItemBounds}
        getSprite={scene.current?.getSprite}
      />
    );

  const renderProductMenu = () =>
    !(isMobilePortrait || isTabletPortrait) && (
      <ProductMenu forwardEvent={forwardEvent} />
    );

  const renderPortraitProductMenu = () =>
    (isMobilePortrait || isTabletPortrait) && (
      <div id="mobileMenu" className={styles.mobileMenuWrapper}>
        <ProductMenu forwardEvent={forwardEvent} />
      </div>
    );

  /**
   * Render sheet
   */
  const showSheet = () => {
    const sheet: SheetObject = {
      sheetType: sceneSheetVariant,
    };
    showingSceneSheet && dispatch(actionEnqueueSheet(sheet));
  };

  return (
    <JoyRide queue={getQueue()}>
      {showSheet()}
      <div
        id={SCENE_VIEW_ID}
        ref={root}
        className={classNames(styles.sceneView, isDragging && styles.dragging)}
        onMouseUp={MOUSE_SUPPORT ? stopDrag : undefined}
        onPointerUp={POINTER_SUPPORT ? stopDrag : undefined}
        onTouchEnd={TOUCH_SUPPORT ? stopDrag : undefined}
        onTouchMove={isDragging && TOUCH_SUPPORT ? abortIfTouchExit : undefined}
        onMouseLeave={isDragging && MOUSE_SUPPORT ? abortDrag : undefined}
        onPointerLeave={isDragging && POINTER_SUPPORT ? abortDrag : undefined}
      >
        {renderWallResizerInput()}
        <div ref={leftContainer} className={styles.leftContainer}>
          <TopBar />
          <div
            id="canvas-container"
            className={styles.canvasContainer}
            ref={canvasContainer}
          >
            <SupplyBanner />
          </div>

          {renderPortraitProductMenu()}
          <Footer onNextViewClick={onNextViewClick} />
        </div>

        <ProductMenuCover
          ref={productMenuCover}
          show={(!isMobile && wallResizerActive) || showTrashcan}
          trashcan={showTrashcan}
          enlarge={enlargeTrashcan}
        />

        {renderProductMenu()}
        {renderScenePopupManager()}
      </div>
    </JoyRide>
  );
};

export default SceneView;
